import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

function fmtDateTime(s) {
  if (!s) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(s));
}

function fmtDate(s) {
  if (!s) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(s));
}

function daysAgo(s) {
  if (!s) return null;
  const diff = Math.floor((Date.now() - new Date(s).getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff}d ago`;
}

// ── Course block ──────────────────────────────────────────────────────────────
function CourseBlock({ course }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Course header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between bg-slate-50 px-5 py-3.5 text-left hover:bg-slate-100 transition"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
            viewBox="0 0 20 20" fill="currentColor"
          >
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02z" clipRule="evenodd" />
          </svg>
          <span className="font-bold text-slate-800">{course.courseName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Last Upload</p>
            <p className="text-xs font-semibold text-teal-700">{fmtDate(course.latestUpload)}</p>
            <p className="text-[10px] text-slate-400">{daysAgo(course.latestUpload)}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Uploads</p>
            <p className="text-lg font-bold text-slate-700">{course.totalUploads}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Records Updated</p>
            <p className="text-lg font-bold text-orange-600">{course.totalUpdated}</p>
          </div>
        </div>
      </button>

      {/* History table */}
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 bg-white">
              <tr>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400">#</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400">Batch</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400">Updated By</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">Records</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">Date &amp; Time</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase text-slate-400 text-right">When</th>
              </tr>
            </thead>
            <tbody>
              {course.history.map((h, i) => (
                <tr key={h.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-2.5 text-xs text-slate-400">{i + 1}</td>
                  <td className="px-5 py-2.5 text-xs font-medium text-slate-800">{h.batchName || '—'}</td>
                  <td className="px-5 py-2.5 text-xs text-slate-600">{h.updatedBy || '—'}</td>
                  <td className="px-5 py-2.5 text-right">
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                      {h.updatedCount}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right text-xs text-slate-600">{fmtDateTime(h.uploadedAt)}</td>
                  <td className="px-5 py-2.5 text-right text-xs text-slate-400">{daysAgo(h.uploadedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HndTrackingHistory() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    axiosInstance.get('/admin/dashboard/hnd-tracking-history')
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load history.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.courses) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.courses;
    return data.courses.filter(
      (c) => c.courseName?.toLowerCase().includes(q) ||
             c.history.some((h) => h.batchName?.toLowerCase().includes(q) || h.updatedBy?.toLowerCase().includes(q))
    );
  }, [data, search]);

  const totalUploads  = data?.courses?.reduce((n, c) => n + c.totalUploads, 0)  ?? 0;
  const totalUpdated  = data?.courses?.reduce((n, c) => n + c.totalUpdated, 0)  ?? 0;
  const totalCourses  = data?.courses?.length ?? 0;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100">
              <svg className="h-5 w-5 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">HND Tracking Sheet Upload History</h1>
              <p className="text-xs text-slate-500">Course-wise log of all tracking sheet uploads · newest first</p>
            </div>
          </div>
          {/* Search */}
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 shadow-sm">
            <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search course, batch, staff…"
              className="w-52 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none"
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {/* Summary cards */}
        {!loading && !error && data && (
          <div className="mb-6 flex flex-wrap gap-3">
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">HND Courses</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-800">{totalCourses}</p>
            </div>
            <div className="rounded-xl border border-teal-100 bg-teal-50 px-5 py-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-500">Total Uploads</p>
              <p className="mt-0.5 text-2xl font-bold text-teal-700">{totalUploads}</p>
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 px-5 py-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-500">Records Updated</p>
              <p className="mt-0.5 text-2xl font-bold text-orange-700">{totalUpdated}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <svg className="mr-2 h-5 w-5 animate-spin text-teal-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8H4z" />
            </svg>
            <span className="text-sm">Loading history…</span>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <svg className="mb-3 h-12 w-12 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z" />
            </svg>
            <p className="text-sm">{search ? 'No results match your search.' : 'No upload history found yet.'}</p>
            {!search && (
              <p className="mt-1 text-xs">History is recorded each time a tracking sheet is uploaded via HND Assignment Tracking.</p>
            )}
          </div>
        )}

        {!loading && !error && filtered.map((course) => (
          <CourseBlock key={course.courseId} course={course} />
        ))}
      </div>
    </div>
  );
}
